import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';

import type {
  ContestNotificationWidgetProps,
  ContestNotificationWidgetContent,
} from '../components/ContestNotificationWidget/types';
import type { ContestRestart } from '../components/ContestRestartCard/types';
import type { ContestNotification } from '../components/ContestNotificationCard/types';
import type { ContestInvite } from '../components/ContestInviteCard/types';

interface UseContestNotificationWidgetReturn {
  title: string;
  count: number;
  displayItems: ContestNotificationWidgetContent['type'] extends 'invites'
    ? Array<{ invite: ContestInvite; contest: Contest }>
    : ContestNotificationWidgetContent['type'] extends 'restart'
      ? Array<{ restart: ContestRestart }>
      : ContestNotificationWidgetContent['type'] extends 'restart'
        ? Array<{ notification: ContestNotification }>
        : Array<any>;
  hasItems: boolean;
  isInviteMode: boolean;
  isNotificationMode: boolean;
}

export default function useContestNotificationWidget({
  content,
  totalCount,
}: Pick<
  ContestNotificationWidgetProps,
  'content' | 'totalCount'
>): UseContestNotificationWidgetReturn {
  const { t } = useTranslation('contest');

  const { title, count, displayItems, isInviteMode, isNotificationMode } = useMemo(() => {
    if (content.type === 'invites') {
      const inviteCount = content.invites.length;
      const slicedDisplayItems = content.invites.slice(0, 1);

      return {
        title: t('common.contestNotificationWidget.newContestInvites__hasPlural', {
          count: totalCount,
        }),
        count: inviteCount,
        displayItems: slicedDisplayItems,
      };
    }

    if (content.type === 'notifications') {
      const slicedDisplayItems = content.notifications.slice(0, 3);

      return {
        title: t('common.contestNotificationWidget.contestsNeedAttention__hasPlural', {
          count: totalCount,
        }),
        count: content.notifications.length,
        displayItems: slicedDisplayItems,
      };
    }

    if (content.type === 'offers') {
      const slicedDisplayItems = content.offers.slice(0, 1);

      return {
        title: t('common.offerFeed.title__hasPlural', { count: totalCount }),
        count: content.offers.length,
        displayItems: slicedDisplayItems,
      };
    }

    if (content.type === 'groupInvites') {
      return {
        title: t('common.contestNotificationWidget.groupEntryRequests__hasPlural', {
          count: totalCount,
        }),
        count: content.groupInvites.length,
        displayItems: content.groupInvites,
      };
    }

    if (content.type === 'restart') {
      const slicedDisplayItems = content.restarts.slice(0, 2);

      return {
        title: t('common.contestNotificationWidget.contestRestarts__hasPlural', {
          count: totalCount,
        }),
        count: content.restarts.length,
        displayItems: slicedDisplayItems,
      };
    }

    return {
      title: '',
      count: 0,
      displayItems: [],
      isInviteMode: false,
      isNotificationMode: false,
    };
  }, [content, t, totalCount]);

  const hasItems = count > 0;

  return {
    title,
    count,
    displayItems,
    hasItems,
    isInviteMode,
    isNotificationMode,
  };
}
